import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Card, LineChart, Badge } from '@tremor/react';
import { getRetencaoRecompra, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /retencao/recompra) ───────────────────────────────

interface JanelaPct {
  dias: number;
  num: number;
  denom: number;
  pct: number | null;
}

interface PontoDia {
  dia: number;
  num: number;
  denom: number;
  pct: number | null;
}

interface RecompraData {
  disponivel: boolean;
  erro?: string;
  janela_horas: number;
  pagantes: number;
  compras_brutas: number;
  eventos_compra: number;
  recompraram: number;
  recompra_pct: number | null;
  recompra_60d: JanelaPct;
  recompra_90d: JanelaPct;
  mediana_dias_1a_2a: number | null;
  dist_eventos: { eventos: string; n: number }[];
  serie: PontoDia[];
  caveat: string;
}

type Janela = '24' | '0';

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function Toggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`rounded px-2.5 py-1 text-xs transition ${
            value === o.v
              ? 'bg-blue-600/30 text-blue-200'
              : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2">
      <Text className="text-[11px] uppercase tracking-wide text-slate-400">
        {label}
      </Text>
      <div className="text-xl font-semibold text-slate-100">{value}</div>
      <Text className="text-[11px] text-slate-500">{hint}</Text>
    </div>
  );
}

export function RecompraCard() {
  const [janela, setJanela] = useState<Janela>('24');

  const { data, isLoading, error } = useQuery<RecompraData>({
    queryKey: ['retencao-recompra', janela],
    queryFn: async () =>
      (await getRetencaoRecompra({ janela_horas: Number(janela) })).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar recompra')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const serieLabel = '% já fez a 2ª compra';
  const chartData = useMemo(
    () =>
      (data?.serie ?? []).map((p) => ({
        dia: `d${p.dia}`,
        [serieLabel]: p.pct === null ? null : +(p.pct * 100).toFixed(1),
        'n maduro': p.denom,
      })),
    [data],
  );

  const dist = (data?.dist_eventos ?? [])
    .map((d) => `${d.eventos} compra${d.eventos === '1' ? '' : 's'}: ${d.n}`)
    .join(' · ');

  return (
    <section className="space-y-2">
      <Text className="text-xs uppercase tracking-wide text-slate-400">
        Recompra (retenção monetária)
      </Text>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <Title>O pagante volta a comprar?</Title>
            <Text className="text-xs text-slate-400">
              Só pagamento <b>Stripe real</b> (grant/ajuste não conta). Curva =
              % dos pagantes que <b>já fizeram a 2ª compra</b> até o dia n desde
              a própria 1ª compra. Denominador maduro: no dia n só conta quem já
              vive n dias desde a 1ª compra.
            </Text>
          </div>
          <Toggle<Janela>
            value={janela}
            onChange={setJanela}
            options={[
              { v: '24', label: 'recompra real (outro dia)' },
              { v: '0', label: 'compras brutas' },
            ]}
          />
        </div>

        <div className="mb-3 rounded border border-sky-700/40 bg-sky-900/10 px-3 py-2 text-[11px] leading-relaxed text-sky-200/90">
          ℹ️ <b>Por que recompra?</b> Modelo por crédito não tem churn de
          assinatura — o veredito de retenção com referência externa é a 2ª
          compra. Referência de mercado (SaaS transacional, direcional): 2ª
          compra em ≤60d acima de ~30–40% = bom.{' '}
          {janela === '24' && (
            <>
              <b>Compras a &lt;24h da anterior contam como UMA</b> (2 pacotes na
              mesma sessão de checkout ≠ recompra).
            </>
          )}
        </div>

        {isLoading && <Text className="mt-3">carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}

        {!isLoading && !errMsg && data?.disponivel && (
          <>
            <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi
                label="Pagantes (Stripe)"
                value={fmtInt(data.pagantes)}
                hint={`${fmtInt(data.compras_brutas)} compras · ${fmtInt(
                  data.eventos_compra,
                )} eventos`}
              />
              <Kpi
                label="Recompraram (ever)"
                value={fmtPct(data.recompra_pct)}
                hint={`${fmtInt(data.recompraram)} de ${fmtInt(
                  data.pagantes,
                )} · cresce com o tempo`}
              />
              <Kpi
                label="2ª compra ≤60d"
                value={fmtPct(data.recompra_60d?.pct)}
                hint={`${fmtInt(data.recompra_60d?.num)} de ${fmtInt(
                  data.recompra_60d?.denom,
                )} maduros 60d · benchmark ~30–40%`}
              />
              <Kpi
                label="Mediana 1ª→2ª"
                value={
                  data.mediana_dias_1a_2a === null
                    ? '—'
                    : `${data.mediana_dias_1a_2a}d`
                }
                hint={dist || 'distribuição n/d'}
              />
            </div>

            {chartData.length > 1 ? (
              <LineChart
                className="mt-1 h-64"
                data={chartData}
                index="dia"
                categories={[serieLabel]}
                colors={['emerald']}
                valueFormatter={(v) => `${v}%`}
                showAnimation
                connectNulls={false}
                minValue={0}
              />
            ) : (
              <Text className="text-sm text-slate-400">
                série curta demais para o chart (pagantes muito recentes)
              </Text>
            )}

            <Text className="mt-3 block text-[11px] leading-relaxed text-slate-500">
              {data.caveat}
            </Text>
          </>
        )}
      </Card>
    </section>
  );
}
